import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Tooltip,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  getProjectContacts,
  createProjectContact,
  updateProjectContact,
  deleteProjectContact,
  searchCrmContacts,
} from '../services/api';

const CONTACT_ROLES = [
  { value: 'architect', label: 'Architect', color: '#1565C0', icon: '📐' },
  { value: 'builder', label: 'Builder / GC', color: '#2E7D32', icon: '🏗️' },
  { value: 'interior_designer', label: 'Interior Designer', color: '#7B1FA2', icon: '🎨' },
  { value: 'electrician', label: 'Electrician', color: '#E65100', icon: '⚡' },
  { value: 'client_rep', label: 'Client Rep', color: '#00695C', icon: '🤝' },
];

const TITLE_OPTIONS = [
  'Architect',
  'Principal',
  'Project Architect',
  'Builder',
  'General Contractor',
  'Project Manager',
  'Superintendent',
  'Interior Designer',
  'Design Lead',
  'Electrician',
  'Electrical Foreman',
  'Client Representative',
  'Owner',
  'Owner\'s Representative',
  'PM',
  'Designer',
];

const getRoleConfig = (role) =>
  CONTACT_ROLES.find((r) => r.value === role) || { label: role, color: '#666', icon: '👤' };

const emptyForm = {
  role: '',
  company_name: '',
  contact_name: '',
  title: '',
  email: '',
  phone_office: '',
  phone_mobile: '',
  notes: '',
  zoho_contact_id: null,
  zoho_account_id: null,
};

const ContactCard = ({ contact, onEdit, onDelete }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: 'grey.50',
        '&:hover': { bgcolor: 'grey.100' },
        '&:hover .contact-actions': { opacity: 1 },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="body1" fontWeight="bold" noWrap>
            {contact.contact_name}
          </Typography>
          {contact.title && (
            <Typography variant="caption" color="text.secondary">
              — {contact.title}
            </Typography>
          )}
        </Box>

        {contact.company_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {contact.company_name}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {contact.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography
                variant="body2"
                component="a"
                href={`mailto:${contact.email}`}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {contact.email}
              </Typography>
            </Box>
          )}
          {(contact.phone_office || contact.phone) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.disabled" sx={{ mr: 0.25 }}>Office</Typography>
              <Typography
                variant="body2"
                component="a"
                href={`tel:${contact.phone_office || contact.phone}`}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {contact.phone_office || contact.phone}
              </Typography>
            </Box>
          )}
          {contact.phone_mobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.disabled" sx={{ mr: 0.25 }}>Mobile</Typography>
              <Typography
                variant="body2"
                component="a"
                href={`tel:${contact.phone_mobile}`}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {contact.phone_mobile}
              </Typography>
            </Box>
          )}
        </Box>

        {contact.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
            {contact.notes}
          </Typography>
        )}
      </Box>

      <Box
        className="contact-actions"
        sx={{ display: 'flex', gap: 0.5, opacity: 0, transition: 'opacity 0.15s' }}
      >
        <Tooltip title="Edit">
          <IconButton size="small" onClick={() => onEdit(contact)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove">
          <IconButton size="small" color="error" onClick={() => onDelete(contact)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const ProjectContacts = ({ projectId }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedRoles, setExpandedRoles] = useState({});

  // CRM contacts autocomplete state (native Zoho CRM lookup)
  const [crmSuggestions, setCrmSuggestions] = useState([]);
  const [crmSearching, setCrmSearching] = useState(false);
  const [nameInput, setNameInput] = useState('');
  // crmLinked is derived from form.zoho_contact_id below — single source of truth

  const loadContacts = useCallback(async () => {
    try {
      const res = await getProjectContacts(projectId);
      setContacts(res.data);
      // Auto-expand roles that have contacts
      const roles = [...new Set(res.data.map((c) => c.role))];
      setExpandedRoles((prev) => {
        const next = { ...prev };
        roles.forEach((r) => {
          if (next[r] === undefined) next[r] = true;
        });
        return next;
      });
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Debounced CRM contact search (Zoho CRM mirror via API)
  useEffect(() => {
    if (!dialogOpen || editingContact || nameInput.length < 2) {
      setCrmSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      setCrmSearching(true);
      try {
        const res = await searchCrmContacts(projectId, nameInput);
        setCrmSuggestions(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        setCrmSuggestions([]);
      } finally {
        setCrmSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [nameInput, dialogOpen, editingContact, projectId]);

  const toggleRole = (role) => {
    setExpandedRoles((prev) => ({ ...prev, [role]: !prev[role] }));
  };

  const openAdd = (role = '') => {
    setEditingContact(null);
    setForm({ ...emptyForm, role });
    setNameInput('');
    setCrmSuggestions([]);
    setDialogOpen(true);
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      role: contact.role,
      company_name: contact.company_name || '',
      contact_name: contact.contact_name,
      title: contact.title || '',
      email: contact.email || '',
      phone_office: contact.phone_office || contact.phone || '',
      phone_mobile: contact.phone_mobile || '',
      notes: contact.notes || '',
      zoho_contact_id: contact.zoho_contact_id || null,
      zoho_account_id: contact.zoho_account_id || null,
    });
    setNameInput(contact.contact_name);
    setDialogOpen(true);
  };

  // Pre-fill form from a CRM (Zoho) suggestion. Carries zoho_id so the
  // project contact stays linked to the source CRM record.
  const applyCrmContact = (gc) => {
    if (!gc) return;
    setForm((f) => ({
      ...f,
      contact_name: gc.name || f.contact_name,
      company_name: gc.company || f.company_name,
      title: gc.title || f.title,
      email: gc.email || f.email,
      phone_office: gc.phone_office || f.phone_office,
      phone_mobile: gc.phone_mobile || f.phone_mobile,
      zoho_contact_id: gc.zoho_module === 'Contact' ? gc.zoho_id : null,
      zoho_account_id: gc.zoho_module === 'Account' ? gc.zoho_id : (gc.account_zoho_id || null),
    }));
    setNameInput(gc.name || '');
    setCrmSuggestions([]);
  };

  const handleSave = async () => {
    if (!form.role || !form.contact_name.trim()) return;
    setSaving(true);
    try {
      if (editingContact) {
        await updateProjectContact(projectId, editingContact.id, form);
      } else {
        await createProjectContact(projectId, form);
      }
      setDialogOpen(false);
      loadContacts();
    } catch (err) {
      console.error('Save contact error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteProjectContact(projectId, deleteConfirm.id);
      setDeleteConfirm(null);
      loadContacts();
    } catch (err) {
      console.error('Delete contact error:', err);
    }
  };

  // Group contacts by role
  const grouped = CONTACT_ROLES.map((role) => ({
    ...role,
    contacts: contacts.filter((c) => c.role === role.value),
  }));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
          Trade Partners & Contacts
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => openAdd()}
        >
          Add Contact
        </Button>
      </Box>

      {grouped.map((group) => (
        <Box key={group.value} sx={{ mb: 1.5 }}>
          <Box
            onClick={() => toggleRole(group.value)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1,
              px: 1.5,
              cursor: 'pointer',
              borderRadius: 1,
              borderLeft: `4px solid ${group.color}`,
              bgcolor: group.contacts.length > 0 ? `${group.color}08` : 'transparent',
              '&:hover': { bgcolor: `${group.color}12` },
            }}
          >
            <Typography variant="body2" sx={{ fontSize: 16 }}>
              {group.icon}
            </Typography>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
              {group.label}
            </Typography>
            <Chip
              label={group.contacts.length}
              size="small"
              variant={group.contacts.length > 0 ? 'filled' : 'outlined'}
              sx={{
                bgcolor: group.contacts.length > 0 ? `${group.color}20` : undefined,
                color: group.contacts.length > 0 ? group.color : 'text.secondary',
                fontWeight: 'bold',
                minWidth: 28,
              }}
            />
            <Tooltip title="Add">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openAdd(group.value);
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {expandedRoles[group.value] ? (
              <ExpandLessIcon fontSize="small" color="action" />
            ) : (
              <ExpandMoreIcon fontSize="small" color="action" />
            )}
          </Box>

          <Collapse in={expandedRoles[group.value]}>
            <Box sx={{ pl: 3, pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {group.contacts.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 1, py: 1 }}>
                  No contacts added yet
                </Typography>
              ) : (
                group.contacts.map((contact) => (
                  <ContactCard
                    key={contact.id}
                    contact={contact}
                    onEdit={openEdit}
                    onDelete={setDeleteConfirm}
                  />
                ))
              )}
            </Box>
          </Collapse>
        </Box>
      ))}

      {contacts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            No trade contacts added to this project yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add architects, builders, designers, and electricians associated with this project.
          </Typography>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Contact' : 'Add Contact'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Role</InputLabel>
              <Select
                value={form.role}
                label="Role"
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              >
                {CONTACT_ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{r.icon}</span> {r.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Contact Name with native CRM (Zoho) lookup */}
            <Autocomplete
              freeSolo
              options={crmSuggestions}
              filterOptions={(x) => x}  /* server-driven results, don't refilter */
              getOptionLabel={(opt) =>
                typeof opt === 'string' ? opt : `${opt.name || ''}${opt.company ? ` — ${opt.company}` : ''}`
              }
              isOptionEqualToValue={(o, v) => o?.zoho_id === v?.zoho_id}
              inputValue={nameInput}
              onInputChange={(_, val, reason) => {
                setNameInput(val);
                setForm((f) => {
                  /* If the user is typing manually (input event), clear any
                     prior CRM link because they're editing the name. If the
                     change came from selecting an option (reset event), keep
                     the link — applyCrmContact already set it correctly. */
                  if (reason === 'input') {
                    return { ...f, contact_name: val, zoho_contact_id: null, zoho_account_id: null };
                  }
                  return { ...f, contact_name: val };
                });
              }}
              onChange={(_, val) => {
                if (val && typeof val === 'object') applyCrmContact(val);
              }}
              loading={crmSearching}
              renderOption={(props, opt) => (
                <Box component="li" {...props} key={opt.zoho_id || opt.id}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight="bold">{opt.name || '—'}</Typography>
                      <Chip
                        label={opt.zoho_module === 'Contact' ? 'Contact' : 'Account'}
                        size="small"
                        sx={{
                          height: 16, fontSize: '0.65rem',
                          bgcolor: opt.zoho_module === 'Contact' ? 'primary.50' : 'warning.50',
                          color: opt.zoho_module === 'Contact' ? 'primary.main' : 'warning.dark',
                        }}
                      />
                    </Box>
                    {(opt.title || opt.company) && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {[opt.title, opt.company].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                    {(opt.email || opt.phone_office) && (
                      <Typography variant="caption" color="text.disabled" display="block">
                        {[opt.email, opt.phone_office].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => {
                /* MUI v9: renderInput params no longer expose `InputProps`.
                   Use slotProps.input or just spread params directly into
                   TextField. The loading spinner is rendered via the
                   Autocomplete `loading` prop and its built-in indicator. */
                return (
                  <TextField
                    {...params}
                    label="Contact Name"
                    size="small"
                    required
                    placeholder="Search Zoho CRM by name, company, or email…"
                  />
                );
              }}
            />

            {/* CRM link / push-back hint (derived from form.zoho_contact_id) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: -1 }}>
              {form.zoho_contact_id ? (
                <Chip
                  size="small"
                  label={`Linked to Zoho CRM contact (#${String(form.zoho_contact_id).slice(-6)})`}
                  color="success"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              ) : form.contact_name && (form.email || form.company_name) && !editingContact ? (
                <Typography variant="caption" color="primary.main">
                  ✨ Not in CRM yet — will be added to Zoho CRM on save
                </Typography>
              ) : null}
            </Box>

            <TextField
              label="Company / Firm"
              size="small"
              value={form.company_name}
              onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
              placeholder="Smith Architecture LLC"
            />

            {/* Title — dropdown with free-text option */}
            <Autocomplete
              freeSolo
              options={TITLE_OPTIONS}
              value={form.title}
              onInputChange={(_, val) => setForm((f) => ({ ...f, title: val }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Title"
                  size="small"
                  placeholder="Architect, Principal, GC, etc."
                />
              )}
            />

            <TextField
              label="Email"
              size="small"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="john@firm.com"
            />

            {/* Separate phone fields */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Office Phone"
                size="small"
                fullWidth
                value={form.phone_office}
                onChange={(e) => setForm((f) => ({ ...f, phone_office: e.target.value }))}
                placeholder="(212) 555-1234"
              />
              <TextField
                label="Mobile Phone"
                size="small"
                fullWidth
                value={form.phone_mobile}
                onChange={(e) => setForm((f) => ({ ...f, phone_mobile: e.target.value }))}
                placeholder="(917) 555-5678"
              />
            </Box>

            <TextField
              label="Notes"
              size="small"
              multiline
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Primary point of contact for site access, preferred communication method, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.role || !form.contact_name.trim()}
          >
            {saving ? <CircularProgress size={20} /> : editingContact ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Remove Contact</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteConfirm?.contact_name}</strong>
            {deleteConfirm?.company_name ? ` (${deleteConfirm.company_name})` : ''} from this project?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectContacts;
